import React, { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';

import { Box, DialogContent, TextField, Alert } from '@mui/material';
import { useParams } from 'react-router';
import { useAppDispatch } from '../../../hooks/hooks';
import { updateReduxSite } from '../../../redux/actions/siteActions';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { SiteDto, SituationDto } from '../../../api/ClientApi';
import { WarehouseClient } from '../../../api/Extensions';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

export interface SituationFormProps {
    open: boolean;
    closeForm(): void;
}

export default function SituationForm(props: SituationFormProps): ReactElement {
    const [isUpdate, setIsUpdate] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [saveStatus, setSaveStatus] = useState<SaveEntityStatus>(SaveEntityStatus.None);

    const [situation, setSituation] = useState<SituationDto>(new SituationDto());
    const [site, setSite] = useState<SiteDto | undefined>(undefined);

    const { open, closeForm } = props;

    const dispatch = useAppDispatch();

    const params = useParams();

    useEffect(() => {
        if (open) {
            const siteId = params.siteId;
            if (!!siteId && !isNaN(+siteId) && +siteId > 0) {
                WarehouseClient.getSiteById(+siteId, false, false).then((res) => setSite(res));
            }
        }
    }, [params, open]);

    const handleChangeSituationTitle = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        const newSituation = new SituationDto();
        newSituation.init(situation);
        newSituation.title = value;

        setSituation(newSituation);
    };

    const handleClose = () => {
        setSituation(new SituationDto());
        setErrorMessage(undefined);
        closeForm();
    };

    const isSituationValid = !!situation.title && situation.title.trim() !== '';

    const submit = () => {
        const siteId = params.siteId;

        if (!!siteId && !isNaN(+siteId) && +siteId > 0) {
            setSaveStatus(SaveEntityStatus.Pending);
            WarehouseClient.addNewSituationForSite(+siteId, situation).then(
                () => {
                    dispatch(updateReduxSite(+siteId ?? 0));
                    setSaveStatus(SaveEntityStatus.Saved);
                    closeForm();
                },
                (err) => {
                    setSaveStatus(SaveEntityStatus.Error);
                    console.log(err);
                    const message =
                        err.MessageCode === 'EXCEPTION_EXISTING_SITUATION_FOR_SITE'
                            ? 'Situacija već postoji za izabrano gradilište'
                            : '';

                    setErrorMessage(message);
                },
            );
        }
    };

    const wordOfTheDay = isUpdate ? 'Izmeni' : 'Kreiraj';

    const siteName = useMemo(() => {
        return !site ? '/' : site.name;
    }, [site]);

    return (
        <ResponsiveDialog scroll="body" open={open} onClose={handleClose}>
            <FormDialogTitle>
                {wordOfTheDay} situaciju za {siteName}
            </FormDialogTitle>
            <DialogContent
                dividers
                sx={{
                    width: 500,
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                }}
            >
                <Box>
                    <TextField
                        required
                        error={!situation.title || situation.title.trim() === ''}
                        fullWidth
                        name="title"
                        label="Naziv"
                        value={situation.title}
                        variant="outlined"
                        onChange={handleChangeSituationTitle}
                    />
                </Box>

                <FormActions
                    onCancel={handleClose}
                    onSubmit={submit}
                    submitLabel="Sačuvaj"
                    disabled={!isSituationValid || saveStatus === SaveEntityStatus.Pending}
                    pending={saveStatus === SaveEntityStatus.Pending}
                    inGrid={false}
                />
                {saveStatus === SaveEntityStatus.Error && (
                    <Alert
                        severity="warning"
                        onClose={() => {
                            setErrorMessage(undefined);
                            setSaveStatus(SaveEntityStatus.None);
                        }}
                    >
                        {errorMessage}
                    </Alert>
                )}
            </DialogContent>
        </ResponsiveDialog>
    );
}
